import React, { useMemo, useState } from "react"

import { PracticePayerMappingsBulkMode } from "@common-shared/config/practice.config"

import { InfoNoticeElement } from "@front-shared/elements/infoNotice.element"
import { NirvanaBreathingLoaderElement } from "@front-shared/elements/nirvanaBreathingLoader.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { PAYER_MAPPINGS_CONFIG } from "../../../../config/payerMappings.config"
import { PayerMappingsCsvHelper } from "../../../../helpers/payerMappingsCsv.helper"
import { RuntimeHelper } from "../../../../helpers/runtime.helper"
import { useClientPaginatedReview } from "../../../../hooks/clientPaginatedReview.hook"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { payerMappingsSelector } from "../../../../store/selectors/payerMappings.selector"
import { runtimeSelector } from "../../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../../store/selectors/selectedPracticeRole.selector"
import { payerMappingsUpsertBulk } from "../../../../store/thunks/payerMappings.thunks"
import type { IReviewPayerMappingsState } from "../../../../types/payerMappings.types"
import { CsvReviewFloatingControlComponent } from "../../../general/csvUploads/csvReviewFloatingControl.component"
import { CsvReviewPageHeaderComponent } from "../../../general/csvUploads/csvReviewPageHeader.component"
import { PoliciesListPaginationComponent } from "../../../policies/policiesList/policiesListPagination.component"
import { PayerMappingsBulkUploadPopupComponent } from "../../../popups/payerMappingsBulkUploadPopup.component"
import { PayerMappingsTableHeaderComponent } from "./payerMappingsTableHeader.component"
import { PayerMappingsTableRowComponent } from "./payerMappingsTableRow.component"

export const PayerMappingsReviewComponent: React.FunctionComponent<
    IPayerMappingsReviewComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const payerMappingsState = useAppSelector(payerMappingsSelector)

    const isSubmitting = useMemo<boolean>(() => {
        return RuntimeHelper.isPayerMappingsUpsertBulkLoading()
    }, [runtimeState.isLoading])

    const [isReuploadPopupActive, setIsReuploadPopupActive] =
        useState<boolean>(false)

    const hasExistingMappings = payerMappingsState.unfilteredItemsTotal > 0

    const {
        errorByIndex,
        getNewItems,
        nextPagination,
        pageItems,
        resetReviewStart,
        reviewClampedStart,
        reviewTotal,
        selectedPaginationOption,
        setSelectedPaginationOption,
        validItems: validItemsToSend,
    } = useClientPaginatedReview(
        props.state.items,
        props.state.errors,
        PAYER_MAPPINGS_CONFIG.reviewListPerPageStorageKey,
    )

    const onReuploadCsv = () => {
        setIsReuploadPopupActive(true)
    }

    const onReuploadDone = (data: IReviewPayerMappingsState) => {
        setIsReuploadPopupActive(false)
        resetReviewStart()

        props.onReuploadDone(data)
    }

    const upsertValidItems = (bulkMode: PracticePayerMappingsBulkMode) => {
        if (!selectedPracticeRole || !validItemsToSend.length) {
            return
        }

        dispatch(
            payerMappingsUpsertBulk({
                practice: selectedPracticeRole.practice,
                payload: {
                    mode: bulkMode,
                    items: PayerMappingsCsvHelper.toRequestItems(
                        validItemsToSend,
                    ),
                },
                onSuccess: () => {
                    ToastrHelper.success(
                        bulkMode === PracticePayerMappingsBulkMode.REPLACE
                            ? "Payer mappings were replaced"
                            : "Payer mappings were added",
                    )

                    props.onUpsertSuccess()
                },
            }),
        )
    }

    const getUpsertModalContentCopy = (
        bulkMode: PracticePayerMappingsBulkMode,
    ) => {
        const errorCount = props.state.errors.length
        const isErrorsPlural = errorCount > 1

        const updatedCount = props.state.updatedCount
        const isUpdatedCountPlural = updatedCount > 1

        const createdCount = props.state.createdCount
        const isCreatedCountPlural = createdCount > 1

        // Validation ran over every parsed row, so its own deleted count spares the
        // mappings behind the invalid ones. Those rows are dropped from the upsert,
        // so under REPLACE everything not being updated goes - errors included
        const deletedCount = Math.max(
            0,
            payerMappingsState.unfilteredItemsTotal - updatedCount,
        )
        const isDeletedCountPlural = deletedCount > 1

        return (
            <>
                {/* A caveat about the upload, not an outcome of it, so it sits
                    above the list rather than reading as a fourth bullet */}
                {errorCount > 0 && (
                    <PrimaryText
                        className="text-left mb-[12px]"
                        weight="medium"
                    >
                        {`${errorCount} invalid mapping${isErrorsPlural ? "s" : ""} found and won't be processed. Fix and re-upload to include ${isErrorsPlural ? "them" : "it"}.`}
                    </PrimaryText>
                )}

                <ul className="pl-[20px]">
                    <li className="text-left list-disc marker:text-lg">
                        <PrimaryText>
                            {createdCount
                                ? `${createdCount} new payer mapping${isCreatedCountPlural ? "s" : ""} will be added.`
                                : "No new payer mappings will be added."}
                        </PrimaryText>
                    </li>

                    <li className="text-left list-disc marker:text-lg">
                        <PrimaryText>
                            {updatedCount
                                ? `${updatedCount} existing payer mapping${isUpdatedCountPlural ? "s" : ""} will be updated.`
                                : "No existing payer mappings will be updated."}
                        </PrimaryText>
                    </li>

                    <li className="text-left list-disc marker:text-lg">
                        <PrimaryText>
                            {deletedCount > 0 &&
                            bulkMode === PracticePayerMappingsBulkMode.REPLACE
                                ? `${deletedCount} existing mapping${isDeletedCountPlural ? "s" : ""} will be permanently removed${errorCount > 0 ? ", including any matched by an invalid row" : ""}.`
                                : "No existing mappings will be removed."}
                        </PrimaryText>
                    </li>
                </ul>

                <InfoNoticeElement className="mt-[16px] text-left">
                    {PAYER_MAPPINGS_CONFIG.bulkUpsertDurationNotice}
                </InfoNoticeElement>
            </>
        )
    }

    const onAddValidMappings = () => {
        const validItemCount = validItemsToSend.length
        const isValidItemsPlural = validItemCount > 1

        const bulkMode = PracticePayerMappingsBulkMode.ADDITIVE

        ConfirmationHelper.enableConfirmation(
            () => {
                upsertValidItems(bulkMode)
            },
            `Add ${validItemCount} payer mapping${isValidItemsPlural ? "s" : ""} from this CSV?`,
            getUpsertModalContentCopy(bulkMode),
            undefined,
            undefined,
            undefined,
            true,
            {
                isLeftAligned: false,
                width: 600,
            },
        )
    }

    const onReplaceValidMappings = () => {
        const payerMappingCount = payerMappingsState.unfilteredItemsTotal
        const isPayerMappingCountPlural = payerMappingCount > 1

        const validItemCount = validItemsToSend.length

        const bulkMode = PracticePayerMappingsBulkMode.REPLACE

        ConfirmationHelper.enableConfirmation(
            () => {
                upsertValidItems(bulkMode)
            },
            `Replace ${payerMappingCount} existing payer mapping${isPayerMappingCountPlural ? "s" : ""} with ${validItemCount} from this CSV?`,
            getUpsertModalContentCopy(bulkMode),
            undefined,
            undefined,
            undefined,
            true,
            {
                isLeftAligned: false,
                width: 600,
            },
        )
    }

    return (
        <div className="pb-32 relative">
            <NirvanaBreathingLoaderElement
                isActive={isSubmitting}
                subContent={
                    <InfoNoticeElement>
                        {PAYER_MAPPINGS_CONFIG.bulkUpsertDurationNotice}
                    </InfoNoticeElement>
                }
            />

            <CsvReviewPageHeaderComponent
                title="Review Payer Mappings"
                filename={props.state.filename}
                onClose={props.onClose}
            />

            <div className="relative pb-[120px]">
                <PayerMappingsTableHeaderComponent />

                {pageItems.length === 0 ? (
                    <PrimaryText className="mt-[24px] text-center opacity-50">
                        No payer mappings found
                    </PrimaryText>
                ) : (
                    pageItems.map((item, index) => {
                        const itemIndex = reviewClampedStart + index

                        return (
                            <PayerMappingsTableRowComponent
                                key={itemIndex}
                                externalName={item.externalName}
                                externalPayerId={item.externalPayerId}
                                payerId={item.payerId}
                                error={errorByIndex.get(itemIndex)?.error}
                            />
                        )
                    })
                )}

                {reviewTotal > 0 ? (
                    <PoliciesListPaginationComponent
                        alignLeft={true}
                        getNewItems={getNewItems}
                        nextPagination={nextPagination}
                        onPaginationChange={undefined}
                        selectedPaginationOption={selectedPaginationOption}
                        setSelectedPaginationOption={
                            setSelectedPaginationOption
                        }
                    />
                ) : null}
            </div>

            <CsvReviewFloatingControlComponent
                invalidCount={props.state.errors.length}
                totalCount={
                    props.state.totalRowCount ?? props.state.items.length
                }
                validCount={validItemsToSend.length}
                onReuploadCsv={onReuploadCsv}
                onAddValidRows={onAddValidMappings}
                onReplaceValidRows={
                    hasExistingMappings ? onReplaceValidMappings : undefined
                }
                isSubmitting={isSubmitting}
            />

            <PayerMappingsBulkUploadPopupComponent
                isActive={isReuploadPopupActive}
                onDone={() => {
                    setIsReuploadPopupActive(false)
                }}
                onValidationDone={onReuploadDone}
            />
        </div>
    )
}

interface IPayerMappingsReviewComponentProps {
    state: IReviewPayerMappingsState
    onClose: () => void
    onReuploadDone: (data: IReviewPayerMappingsState) => void
    onUpsertSuccess: () => void
}
